import type { InstanceApi } from '@eis/insuranceservice-sdk';

/**
 * The breakdown's rows (SDD-021 §7.1, PC-11, PC-18): what each premium line IS, and which are
 * shown.
 *
 * Three kinds of line, by `source`: a PRODUCT line (a premium item), a GROUP line (a group
 * factor: rounded result minus rounded input, so the lines add up) and a TAX line (the tax EIS
 * derived, or — INCLUDED — the tax contained in the gross, *darin enthalten*). A `HIDDEN` item is a
 * HELPER: computed, stored, never summed. It is shown only on request, and never in a sum row —
 * the sums are the premium's own `net`, `tax` and `gross`, never a total over the rows.
 */
export type LineRole = 'product' | 'group' | 'tax';

export function roleOf(item: InstanceApi.PremiumItem): LineRole {
  if (item.source === 'GROUP') return 'group';
  if (item.source === 'TAX' || item.itemType === 'TAX') return 'tax';
  return 'product';
}

export const isHelper = (item: InstanceApi.PremiumItem): boolean => item.visibility === 'HIDDEN';

export const hasHelpers = (items: readonly InstanceApi.PremiumItem[] | undefined): boolean =>
  (items ?? []).some(isHelper);

export interface BreakdownRow {
  readonly item: InstanceApi.PremiumItem;
  readonly role: LineRole;
  readonly helper: boolean;
  /** The tax line of an INCLUDED premium: contained in the gross, not added below it. */
  readonly included: boolean;
}

export interface BreakdownGroup {
  /** The items' `group` label ('' = ungrouped). */
  readonly group: string;
  readonly rows: readonly BreakdownRow[];
}

/**
 * The rows, grouped by `group` in the order the groups first appear (the premium's evaluation
 * order is kept inside each group). Helpers are left out unless asked for.
 */
export function breakdownGroups(
  items: readonly InstanceApi.PremiumItem[] | undefined,
  showHelpers: boolean,
): BreakdownGroup[] {
  const groups: { group: string; rows: BreakdownRow[] }[] = [];
  for (const item of items ?? []) {
    const helper = isHelper(item);
    if (helper && !showHelpers) continue;
    const group = item.group ?? '';
    let bucket = groups.find((entry) => entry.group === group);
    if (bucket === undefined) {
      bucket = { group, rows: [] };
      groups.push(bucket);
    }
    bucket.rows.push({ item, role: roleOf(item), helper, included: item.included === true });
  }
  return groups;
}

/** The name a product owner chose; the key as the honest last resort. */
export const lineNameOf = (item: InstanceApi.PremiumItem): string =>
  item.name === undefined || item.name === '' ? (item.key ?? '') : item.name;
